// ── Client-side agent permission mode helpers (TPT9063) ──
//
// Mirrors api/src/lib/agent-permission-mode.js (CommonJS, server-only — the SPA can't import
// it). Stored values are the three canonical modes. null on a PROJECT is "Not set" (legacy
// behaviour kept); null on a TASK is "Project Default", resolved when the
// task starts, never copied at creation. No i18n layer in this SPA (see lib/statuses.js header):
// labels and hints are fixed English strings rendered as-is, so nothing here needs escaping.

import { COWBOY_MODES, normalizeCowboyScope } from '../../../src/lib/cowboy-mode.js';
export { COWBOY_MODES, normalizeCowboyScope };

export const AGENT_PERMISSION_MODES = Object.freeze(['plan', 'auto', 'bypass']);

function shieldIcon(mode, detail, outline = 'M8 1.5 3 3.5v4c0 3 2.1 5.3 5 6.5 2.9-1.2 5-3.5 5-6.5v-4Z') {
  return `<svg class="permission-mode-icon permission-mode-icon--${mode}" viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="${outline}"${mode === 'inherited' ? ' stroke-dasharray="2 2"' : ''}/>${detail}</svg>`;
}

// Static, trusted SVGs shared by selects and permission indicators. Inheritance/unset is
// deliberately distinct from the effective mode; all glyphs inherit theme colours via CSS.
export const PERMISSION_MODE_ICONS = Object.freeze({
  inherited: shieldIcon('inherited', '<path d="M6 7.5h4"/>'),
  plan: shieldIcon('plan', '<path d="M6 5.5h4M6 7.5h4M6 9.5h2"/>'),
  auto: shieldIcon('auto', '<path d="m8 4 .8 2.7L11 7.5l-2.2.8L8 11l-.8-2.7L5 7.5l2.2-.8Z"/>'),
  bypass: shieldIcon('bypass', '<path d="M8.5 4 6 8h2l-.5 3L10 7H8Z"/>'),
});

export function permissionModeIcon(mode) {
  return PERMISSION_MODE_ICONS[isPermissionMode(mode) ? mode : 'inherited'];
}

export const PERMISSION_MODE_LABELS = Object.freeze({
  plan: 'Plan mode',
  auto: 'Auto mode',
  bypass: 'Bypass mode',
});

export const PERMISSION_MODE_HINTS = Object.freeze({
  plan: 'The agent plans first and waits for approval before implementing.',
  auto: 'The agent skips planning and starts implementing, asking for permissions when needed.',
  bypass: 'No approval prompts; the task runs unattended.',
});

export const NOT_SET_LABEL = 'Not set';
export const NOT_SET_HINT = 'Until a mode is chosen, agents keep their current behaviour.';

export function isPermissionMode(value) {
  return typeof value === 'string' && AGENT_PERMISSION_MODES.includes(value);
}

export function permissionModeLabel(value) {
  return isPermissionMode(value) ? PERMISSION_MODE_LABELS[value] : NOT_SET_LABEL;
}

export function permissionModeHint(value) {
  return isPermissionMode(value) ? PERMISSION_MODE_HINTS[value] : NOT_SET_HINT;
}

// "Project Default (Auto mode)" / "Project Default (not set)" — the task-level option that
// defers to the project's effective value at run start.
export function projectDefaultOptionLabel(inherited) {
  return `Project Default (${isPermissionMode(inherited) ? PERMISSION_MODE_LABELS[inherited] : 'not set'})`;
}

// IDs and class names are caller-owned constants; labels/icons come only from this module.
export function renderPermissionModeSelect(current, {
  id = 'permission-mode', className = '', disabled = false, projectDefault = null,
} = {}) {
  const selected = isPermissionMode(current) ? current : '';
  const rows = AGENT_PERMISSION_MODES.map(value => ({ value, label: permissionModeLabel(value), icon: permissionModeIcon(value) }));
  if (projectDefault) rows.unshift({ value: '', label: projectDefaultOptionLabel(projectDefault.inherited), icon: permissionModeIcon(null) });
  else if (!selected) rows.unshift({ value: '', label: NOT_SET_LABEL, icon: permissionModeIcon(null), placeholder: true });
  const active = rows.find(row => row.value === selected);
  return `<div class="permission-mode-select">
    <input type="hidden" id="${id}" class="${className}" value="${selected}"${disabled ? ' disabled' : ''}>
    <button type="button" id="${id}-trigger" class="form-select permission-mode-trigger" role="combobox" aria-label="Permission mode" aria-haspopup="listbox" aria-expanded="false" aria-controls="${id}-list"${disabled ? ' disabled' : ''}>${active.icon}<span class="permission-mode-value">${active.label}</span></button>
    <div id="${id}-list" class="permission-mode-list" role="listbox" aria-label="Permission mode" hidden>
      ${rows.map(row => `<button type="button" class="permission-mode-option" role="option" tabindex="-1" data-value="${row.value}" aria-selected="${row.value === selected}"${row.placeholder ? ' data-placeholder="true" disabled' : ''}>${row.icon}<span class="permission-mode-value">${row.label}</span></button>`).join('')}
    </div>
  </div>`;
}

// Repaint after programmatic rollback or while an autosave disables the backing input. Disabling
// the focused trigger drops keyboard focus to the body, so a trigger that had it is marked and
// takes it back once enabled again — unless focus has moved to another control meanwhile.
export function syncPermissionModeSelect(input) {
  const control = input?.closest('.permission-mode-select');
  if (!control) return;
  const trigger = control.querySelector('.permission-mode-trigger');
  if (input.disabled && document.activeElement === trigger) trigger.dataset.refocus = 'true';
  trigger.disabled = input.disabled;
  for (const option of control.querySelectorAll('[role="option"]')) {
    const selected = option.dataset.value === input.value;
    option.setAttribute('aria-selected', String(selected));
    if (option.dataset.placeholder) option.hidden = input.value !== '';
    if (selected) trigger.innerHTML = option.innerHTML;
  }
  if (!input.disabled && trigger.dataset.refocus) {
    delete trigger.dataset.refocus;
    if (document.activeElement === document.body) trigger.focus();
  }
}

// Delegation survives detail-tab repaints. Hidden input change preserves each host's save
// contract; focus stays on the trigger while closed and moves through list options when open.
export function bindPermissionModeSelects(root) {
  let opened = null;
  let typed = '';
  let typedAt = 0;
  const close = (restoreFocus = false) => {
    if (!opened) return;
    opened.querySelector('[role="listbox"]').hidden = true;
    const trigger = opened.querySelector('.permission-mode-trigger');
    trigger.setAttribute('aria-expanded', 'false');
    opened = null;
    if (restoreFocus) trigger.focus();
  };
  const optionsOf = control => [...control.querySelectorAll('[role="option"]')].filter(o => !o.disabled && !o.hidden);
  const open = (control, last = false) => {
    close();
    opened = control;
    control.querySelector('[role="listbox"]').hidden = false;
    control.querySelector('.permission-mode-trigger').setAttribute('aria-expanded', 'true');
    const options = optionsOf(control);
    (last ? options.at(-1) : options.find(o => o.getAttribute('aria-selected') === 'true') || options[0])?.focus();
  };
  const choose = option => {
    const control = option.closest('.permission-mode-select');
    const input = control.querySelector('input');
    if (input.disabled || option.disabled) return;
    const changed = input.value !== option.dataset.value;
    input.value = option.dataset.value;
    syncPermissionModeSelect(input);
    close(true);
    if (changed) input.dispatchEvent(new Event('change', { bubbles: true }));
  };
  const onClick = e => {
    const control = e.target.closest('.permission-mode-select');
    if (!control || !root.contains(control)) { close(); return; }
    if (control.querySelector('input').disabled) return;
    const option = e.target.closest('[role="option"]');
    if (option) choose(option);
    else if (e.target.closest('.permission-mode-trigger')) {
      if (opened === control) close(); else open(control);
    }
  };
  const onKey = e => {
    const control = e.target.closest('.permission-mode-select');
    if (!control || control.querySelector('input').disabled) return;
    const options = optionsOf(control);
    const index = options.indexOf(e.target.closest('[role="option"]'));
    if (e.key === 'Escape' && opened) { e.preventDefault(); e.stopPropagation(); close(true); }
    else if (e.key === 'Tab') close(true);
    else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) {
      e.preventDefault();
      if (opened !== control) open(control, e.key === 'ArrowUp' || e.key === 'End');
      else options[e.key === 'Home' ? 0 : e.key === 'End' ? options.length - 1 : (index + (e.key === 'ArrowUp' ? -1 : 1) + options.length) % options.length]?.focus();
    } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey && e.key !== ' ') {
      e.preventDefault();
      const now = Date.now();
      typed = (now - typedAt < 700 ? typed : '') + e.key.toLowerCase();
      typedAt = now;
      if (opened !== control) open(control);
      options.find(o => o.textContent.trim().toLowerCase().startsWith(typed))?.focus();
    }
  };
  const onFocusOut = e => { if (opened && !opened.contains(e.relatedTarget)) close(); };
  const onOutside = e => { if (opened && !opened.contains(e.target)) close(); };
  root.addEventListener('click', onClick);
  root.addEventListener('keydown', onKey);
  root.addEventListener('focusout', onFocusOut);
  document.addEventListener('pointerdown', onOutside);
  return () => {
    close();
    root.removeEventListener('click', onClick);
    root.removeEventListener('keydown', onKey);
    root.removeEventListener('focusout', onFocusOut);
    document.removeEventListener('pointerdown', onOutside);
  };
}

export function effectivePermissionMode(taskValue, projectValue) {
  return isPermissionMode(taskValue) ? taskValue : isPermissionMode(projectValue) ? projectValue : null;
}

// Cowboy-coding mode (tasks.cowboy_mode, TPT9160): when the task's agent session finishes, it starts
// sessions for the next startable tasks. Same wording as the Task App's Task Edit checkbox.
export const COWBOY_MODE_LABEL = 'Cowboy-Coding mode';
export const COWBOY_MODE_HINT = 'Starts sessions for the next startable tasks when this one finishes. Bypass only.';

// Whether cowboy_mode can be on: only while the EFFECTIVE mode is bypass — the task's override, else
// the project default it inherits. Mirrors the API's cowboyModeAllowed(), which stores false
// otherwise: an override that is set but is not one of the three modes never counts as bypass, even
// over a bypass project default.
export function isCowboyAllowed(taskValue, projectValue) {
  if (taskValue != null && !isPermissionMode(taskValue)) return false;
  return effectivePermissionMode(taskValue, projectValue) === 'bypass';
}

export function renderPermissionModeBadge(taskValue, projectValue) {
  const mode = effectivePermissionMode(taskValue, projectValue);
  const label = `Permission mode: ${isPermissionMode(taskValue) ? permissionModeLabel(mode) : projectDefaultOptionLabel(mode)}`;
  return `<span class="card-permission-mode" data-permission-mode="${mode || ''}" role="img" aria-label="${label}" title="${label}">${permissionModeIcon(mode)}</span>`;
}

// Explains the mode a task will actually start with: its own override, else the project default
// it inherits, else "not set".
export function effectivePermissionModeHint(taskValue, projectValue) {
  if (isPermissionMode(taskValue)) return PERMISSION_MODE_HINTS[taskValue];
  if (isPermissionMode(projectValue)) {
    return `Inherits the project default — ${PERMISSION_MODE_LABELS[projectValue]}: ${PERMISSION_MODE_HINTS[projectValue]}`;
  }
  return `Inherits the project default — not set. ${NOT_SET_HINT}`;
}
